import { __awaiter, __generator } from "tslib";
import * as AugLoopUtils from './utils/AugLoopUtils';
import { SpeechToTextPartialResult, SpeechToTextFinalResult, SpeechSessionEvent, SpeechSessionEventIds, SpeechQualityEvent, DictationSettingsManager, IEventName, VoiceResponseVersion } from '@augloop-types/voice';
export var SpeechToTextResultType;
(function (SpeechToTextResultType) {
    SpeechToTextResultType[SpeechToTextResultType["FinalResult"] = 0] = "FinalResult";
    SpeechToTextResultType[SpeechToTextResultType["PartialResult"] = 1] = "PartialResult";
})(SpeechToTextResultType || (SpeechToTextResultType = {}));
// Represents a client that will receive speech-generated text from AL.
var SpeechToTextClient = /** @class */ (function () {
    function SpeechToTextClient(runtimeManager, fdSpeechClientObserver, useFrontdoorWorkflow, startRecognitionInSpeechToTextClient) {
        var _this = this;
        if (useFrontdoorWorkflow === void 0) { useFrontdoorWorkflow = true; }
        if (startRecognitionInSpeechToTextClient === void 0) { startRecognitionInSpeechToTextClient = true; }
        var _a, _b;
        this.speechSessionEventToken = null;
        this.speechToTextPartialToken = null;
        this.speechToTextFinalResultToken = null;
        this.speechQualityEventToken = null;
        this.useDebugDictation = false;
        this.responseVersion = VoiceResponseVersion.version2;
        this.runtimeManager = runtimeManager;
        this.fdSpeechClientObserver = fdSpeechClientObserver;
        this.useFrontdoorWorkflow = useFrontdoorWorkflow;
        this.startRecognitionInSpeechToTextClient = startRecognitionInSpeechToTextClient;
        this.annotationObserver = {
            onAnnotationResult: function (op) {
                _this.onAnnotationResult(op);
            }
        };
        // add various properties based on query-string parameters
        var queryString = new URLSearchParams((_a = window.location.search) === null || _a === void 0 ? void 0 : _a.toLowerCase());
        var dictationProps = (_b = DictationSettingsManager.dictationSettings.properties, (_b !== null && _b !== void 0 ? _b : {}));
        this.useDebugDictation = queryString.get('wddictationdebugmode') === 'true';
        if (this.useDebugDictation) {
            dictationProps.EnableAudioLogging = 'true';
        }
        var wdSpeechDomain = queryString.get('wdspeechdomain');
        if (wdSpeechDomain) {
            dictationProps.wdSpeechDomain = decodeURIComponent(wdSpeechDomain);
        }
        DictationSettingsManager.dictationSettings = {
            properties: dictationProps
        };
    }
    SpeechToTextClient.prototype.initializeAsync = function (subscribeToResultTypes, sessionTelemetry) {
        if (subscribeToResultTypes === void 0) { subscribeToResultTypes = [SpeechToTextResultType.PartialResult]; }
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var speechSessionEventPromise, speechToTextPartialPromise, speechToTextFinalResultPromise, speechQualityEventPromise, err_1, msg;
            var _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (this.checkAnnotationActive()) {
                            throw new Error('SpeechToTextClient was already initialized');
                        }
                        if (sessionTelemetry)
                            this.sessionTelemetry = sessionTelemetry;
                        _d.label = 1;
                    case 1:
                        _d.trys.push([1, 3, , 4]);
                        speechSessionEventPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechSessionEvent.getTypeName());
                        speechToTextPartialPromise = null;
                        if (subscribeToResultTypes.indexOf(SpeechToTextResultType.PartialResult) >= 0) {
                            speechToTextPartialPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechToTextPartialResult.getTypeName());
                        }
                        speechToTextFinalResultPromise = null;
                        if (subscribeToResultTypes.indexOf(SpeechToTextResultType.FinalResult) >= 0) {
                            speechToTextFinalResultPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechToTextFinalResult.getTypeName());
                        }
                        speechQualityEventPromise = AugLoopUtils.activateAnnotationAsync(this.runtimeManager, SpeechQualityEvent.getTypeName());
                        // add audio logging property when using debug dictation
                        if (this.useDebugDictation) {
                            /* eslint-disable-next-line no-console */
                            console.warn('This dictation session will be logging audio');
                        }
                        return [4 /*yield*/, Promise.all([
                                speechSessionEventPromise,
                                speechToTextPartialPromise,
                                speechToTextFinalResultPromise,
                                speechQualityEventPromise
                            ])];
                    case 2:
                        // Resolve all asynchronously
                        _c = _d.sent(), this.speechSessionEventToken = _c[0], this.speechToTextPartialToken = _c[1], this.speechToTextFinalResultToken = _c[2], this.speechQualityEventToken = _c[3];
                        // Warm up Augloop with empty VoiceTile to avoid initializing Frontdoor twice in Augloop
                        AugLoopUtils.startAudioProcessor(this.runtimeManager, this.useFrontdoorWorkflow, DictationSettingsManager.dictationSettings, this.responseVersion);
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _d.sent();
                        msg = (_a = err_1.message, (_a !== null && _a !== void 0 ? _a : 'SpeechToTextClient.initializeAsync'));
                        (_b = this.sessionTelemetry) === null || _b === void 0 ? void 0 : _b.onAugloopError(msg);
                        this.fdSpeechClientObserver.onError(msg);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    // Uninitialize speech to text client.
    SpeechToTextClient.prototype.uninitializeAsync = function () {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var tokenVarNames, tokenValues, err_2;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        tokenVarNames = [
                            'speechSessionEventToken',
                            'speechToTextPartialToken',
                            'speechToTextFinalResultToken',
                            'speechQualityEventToken'
                        ];
                        tokenValues = tokenVarNames
                            .map(function (tokenVar) { return _this[tokenVar]; })
                            .filter(function (token) { return token !== null; });
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, AugLoopUtils.releaseAnnotations(this.runtimeManager, tokenValues)];
                    case 2:
                        _c.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_2 = _c.sent();
                        (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onAugloopError((_b = err_2.message, (_b !== null && _b !== void 0 ? _b : 'SpeechToTextClient.uninitializeAsync')));
                        return [3 /*break*/, 4];
                    case 4:
                        tokenVarNames.forEach(function (tokenVar) { return (_this[tokenVar] = null); });
                        return [2 /*return*/];
                }
            });
        });
    };
    SpeechToTextClient.prototype.checkAnnotationActive = function () {
        return (this.speechSessionEventToken !== null ||
            this.speechToTextPartialToken !== null ||
            this.speechToTextFinalResultToken !== null ||
            this.speechQualityEventToken !== null);
    };
    // Return an annotation observer implemented by us that app will use to forward annotations to us.
    SpeechToTextClient.prototype.getAnnotationObserver = function () {
        return this.annotationObserver;
    };
    // Handle an annotation result.
    SpeechToTextClient.prototype.onAnnotationResult = function (op) {
        var _this = this;
        var speechSessionEventItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechSessionEvent.getTypeName());
        speechSessionEventItems.map(function (item) {
            _this.handleSpeechSessionEvent(item.body);
        });
        var speechToTextPartialResults = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechToTextPartialResult.getTypeName());
        speechToTextPartialResults.map(function (item) {
            _this.handleSpeechToTextPartialResult(item.body);
        });
        var speechToTextFinalResults = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechToTextFinalResult.getTypeName());
        speechToTextFinalResults.map(function (item) {
            var body = item.body;
            var finalText = body.text;
            sendSpeechToTextFinalResultToSpeechClientObserver(_this.fdSpeechClientObserver, body, finalText, _this.sessionTelemetry);
        });
        var speechQualityEventItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, SpeechQualityEvent.getTypeName());
        speechQualityEventItems.map(function (item) {
            var speechQualityEventResult = item.body;
            if (speechQualityEventResult.eventInfo &&
                speechQualityEventResult.eventInfo.eventName === IEventName.QualityStatusUpdate) {
                _this.handleSpeechQualityEvent(speechQualityEventResult.eventInfo);
            }
        });
    };
    // Handle a voice session event.
    SpeechToTextClient.prototype.handleSpeechSessionEvent = function (speechSessionEvent) {
        switch (speechSessionEvent.eventId) {
            case SpeechSessionEventIds.SpeechRecognitionStarted:
                this.transcriberId = speechSessionEvent.sessionId;
                if (this.useDebugDictation) {
                    /* eslint-disable-next-line no-console */
                    console.warn("Speech transcriberId: " + this.transcriberId);
                }
                // todo: this is temporary. remove once change passing the observer to VoiceSession are in wac
                if (this.startRecognitionInSpeechToTextClient) {
                    this.fdSpeechClientObserver.onSpeechRecognitionStarted();
                }
                break;
            case SpeechSessionEventIds.SpeechRecognitionStopped:
                this.fdSpeechClientObserver.onSpeechEnd();
                break;
            case SpeechSessionEventIds.SpeechSessionStarted:
                this.speechSessionId = speechSessionEvent.sessionId;
                if (this.useDebugDictation) {
                    /* eslint-disable-next-line no-console */
                    console.warn("Speech speechSessionId: " + this.speechSessionId);
                }
                if (this.fdSpeechClientObserver.onSpeechSessionStarted && this.speechSessionId) {
                    var speechSessionStarted = {
                        speechSessionId: this.speechSessionId,
                        transcriptionSignature: ''
                    };
                    this.fdSpeechClientObserver.onSpeechSessionStarted(speechSessionStarted);
                }
                break;
            case SpeechSessionEventIds.SpeechSessionStopped:
                this.fdSpeechClientObserver.onSpeechSessionStopped();
                break;
            default:
                /* eslint-disable-next-line no-console */
                console.warn("Unexpected speechSessionEventId: " + speechSessionEvent.eventId);
                break;
        }
    };
    // Handle a speech to text partial result.
    SpeechToTextClient.prototype.handleSpeechToTextPartialResult = function (speechToTextPartialResult) {
        var _a, _b, _c;
        if (speechToTextPartialResult.text !== '') {
            var text = new Map([
                [speechToTextPartialResult.language, speechToTextPartialResult.text]
            ]);
            (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onPartialResultAnnotationResponse(Date.now(), (_b = speechToTextPartialResult.properties.JsonResult) === null || _b === void 0 ? void 0 : _b.Offset, (_c = speechToTextPartialResult.properties.JsonResult) === null || _c === void 0 ? void 0 : _c.Duration);
            var properties = new Map();
            properties.set('JsonResult', JSON.stringify(speechToTextPartialResult.properties.JsonResult));
            this.fdSpeechClientObserver.onFragmentText(text, properties);
        }
    };
    // Handle a speech quality event.
    SpeechToTextClient.prototype.handleSpeechQualityEvent = function (qualityStatusUpdate) {
        if (this.fdSpeechClientObserver.onEventUpdate) {
            this.fdSpeechClientObserver.onEventUpdate(qualityStatusUpdate);
        }
    };
    return SpeechToTextClient;
}());
export { SpeechToTextClient };
/** We use speechToTextFinalResult to get JSON Properties. But final text can come from
 * speechToTextFinalResult or commandResult.textAnnotation. This method extracts required information
 * And sends to onFinalText observer
 */
export function sendSpeechToTextFinalResultToSpeechClientObserver(fdSpeechClientObserver, speechToTextFinalResult, finalText, sessionTelemetry) {
    var _a, _b;
    var text = new Map([[speechToTextFinalResult.language, finalText]]);
    if (sessionTelemetry) {
        sessionTelemetry.onFinalResultAnnotationResponse(Date.now(), (_a = speechToTextFinalResult.properties.JsonResult) === null || _a === void 0 ? void 0 : _a.Offset, (_b = speechToTextFinalResult.properties.JsonResult) === null || _b === void 0 ? void 0 : _b.Duration);
    }
    var properties = new Map();
    properties.set('JsonResult', JSON.stringify(speechToTextFinalResult.properties.JsonResult));
    fdSpeechClientObserver.onFinalText(text, properties);
}
//# sourceMappingURL=SpeechToTextClient.js.map